"use client";

import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Play,
  Brain,
  Database,
  Layers,
  MessageSquare,
  X
} from 'lucide-react';
import { workflowResolver } from '../services/turfai/workflowResolver';
import { workflowService } from '../services/turfai/workflowService';
import { authService } from '../services/turfai/authService';
import { dmsService } from '../services/turfai/dmsService';

interface DocumentTestCardProps {
  title?: string;
  description?: string;
  onComplete?: (results: any) => void;
}

type WorkflowType = 'classify' | 'extract' | 'classify-extract';

interface WorkflowOption {
  value: WorkflowType;
  label: string;
  description: string;
  icon: React.ElementType;
  registryKey: string;
}

const workflowOptions: WorkflowOption[] = [
  {
    value: 'classify',
    label: 'Classify Only',
    description: 'Identify the document type (Aadhaar, PAN, Passport, etc.)',
    icon: Brain,
    registryKey: 'system-classify'
  },
  {
    value: 'extract',
    label: 'Extract Only',
    description: 'Extract structured data from the document',
    icon: Database,
    registryKey: 'system-extract'
  },
  {
    value: 'classify-extract',
    label: 'Classify & Extract',
    description: 'Identify document type and extract relevant data',
    icon: Layers,
    registryKey: 'system-classify-extract'
  }
];

export function DocumentTestCard({ 
  title = "Document Workflow Test",
  description = "Upload a document and test TurfAI workflows - Classification, Extraction, or Combined processing",
  onComplete 
}: DocumentTestCardProps) {
  // State management
  const [uploadedFiles, setUploadedFiles] = useState<{ id: string; file: File; preview?: string }[]>([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowType>('classify');
  const [selectedPrompt, setSelectedPrompt] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [executionIds, setExecutionIds] = useState<string[]>([]);
  const [allResults, setAllResults] = useState<{ file: string; results: any; error?: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // File upload handling
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const validFiles: { id: string; file: File; preview?: string }[] = [];
    const errors: string[] = [];

    files.forEach(file => {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name}: Invalid file type (JPEG, PNG, or PDF only)`);
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: File size must be less than 10MB`);
        return;
      }

      const fileData: { id: string; file: File; preview?: string } = {
        id: Math.random().toString(36).substr(2, 9),
        file
      };

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          fileData.preview = e.target?.result as string;
        };
        reader.readAsDataURL(file);
      }

      validFiles.push(fileData);
    });

    if (errors.length > 0) {
      setError(errors.join('; '));
      return;
    }

    setUploadedFiles(prev => [...prev, ...validFiles]);
    setError(null);
    setAllResults([]);
  };

  // Remove file function
  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  // Workflow execution for multiple files
  const executeWorkflow = async () => {
    if (uploadedFiles.length === 0) {
      setError('Please upload at least one document');
      return;
    }

    // Validate prompt for extract workflow
    if (selectedWorkflow === 'extract' && !selectedPrompt && !customPrompt.trim()) {
      setError('Please select a prompt or enter a custom prompt for extraction');
      return;
    }

    setProcessing(true);
    setError(null);
    setAllResults([]);
    setExecutionIds([]);
    setProgress(0);

    try {
      // Step 1: Authenticate if needed
      if (!authService.isAuthenticated()) {
        await authService.loginWithTestCredentials();
        setProgress(5);
      }

      // Step 2: Find or create internal collection (hidden from user)
      let currentCollectionId = collectionId;
      if (!currentCollectionId) {
        setProcessingStatus('Finding or creating collection...');
        
        try {
          // First try to find existing collection for test_user
          const existingCollection = await dmsService.getCollectionByEmployeeId('test_user');
          
          if (existingCollection) {
            setProcessingStatus('Using existing collection...');
            currentCollectionId = existingCollection.id;
          } else {
            setProcessingStatus('Creating new collection...');
            const newCollection = await dmsService.createOnboardingCollection({
              employee_id: 'test_user',
              department: 'test',
              start_date: new Date().toISOString(),
              joiner_type: 'employee',
              experience_level: 'experienced',
              name: 'Test User'
            });
            currentCollectionId = newCollection.id;
          }
          
          setCollectionId(currentCollectionId);
          setProgress(10);
        } catch (collectionError) {
          console.error('Collection operation failed:', collectionError);
          throw new Error(`Failed to find or create collection: ${collectionError instanceof Error ? collectionError.message : 'Unknown error'}`);
        }
      }

      // Step 3: Find the selected workflow option
      const workflowOption = workflowOptions.find(opt => opt.value === selectedWorkflow);
      if (!workflowOption) {
        throw new Error('Invalid workflow selection');
      }

      // Step 4: Process each file sequentially
      const results: { file: string; results: any; error?: string }[] = [];
      const execIds: string[] = [];
      
      for (let i = 0; i < uploadedFiles.length; i++) {
        const fileData = uploadedFiles[i];
        setProcessingStatus(`Processing ${fileData.file.name} (${i + 1}/${uploadedFiles.length})`);
        
        try {
          // Upload document to collection
          const document = await dmsService.uploadDocument(currentCollectionId, fileData.file, 'unknown');
          
          // Execute workflow for this document
          
          // Build options with prompt data for extraction workflows AND specificDocuments
          const workflowOptions: any = { 
            workflowType: workflowOption.registryKey,
            specificDocuments: [{
              id: document.id,
              fileUrl: document.attributes?.fileUrl || document.fileUrl,
              title: document.attributes?.title || document.title || fileData.file.name,
              mimeType: document.attributes?.mimeType || document.mimeType || fileData.file.type,
              fileSize: document.attributes?.fileSize || document.fileSize || fileData.file.size
            }]
          };
          
          // Add prompt data for extraction workflows
          if (selectedWorkflow === 'extract' && (selectedPrompt || customPrompt)) {
            workflowOptions.promptId = selectedPrompt === 'custom' ? 'custom' : selectedPrompt;
            workflowOptions.promptText = customPrompt || selectedPrompt;
          }
          
          const execution = await workflowService.executeBatchClassification(
            currentCollectionId,
            {
              employee_id: 'test_user',
              full_name: 'Test User',
              department: 'test',
              joiner_type: 'employee',
              experience_level: 'experienced'
            },
            workflowOptions
          );

          execIds.push(execution.id);
          
          // Poll for results for this execution
          const result = await pollForResults(execution.id);
          
          results.push({
            file: fileData.file.name,
            results: result
          });

        } catch (err) {
          console.error(`Error processing ${fileData.file.name}:`, err);
          results.push({
            file: fileData.file.name,
            results: null,
            error: err instanceof Error ? err.message : 'Unknown error'
          });
        }
        
        // Update progress
        setProgress(10 + ((i + 1) / uploadedFiles.length) * 85);
      }
      
      setExecutionIds(execIds);
      setAllResults(results);
      setProgress(100);
      setProcessingStatus('');
      
      if (onComplete) {
        onComplete({
          totalFiles: uploadedFiles.length,
          results: results,
          workflow: selectedWorkflow
        });
      }
    } catch (err) {
      console.error('Workflow execution error:', err);
      setError(err instanceof Error ? err.message : 'Failed to execute workflow');
      setProcessingStatus('');
    } finally {
      setProcessing(false);
    }
  };

  // Poll for workflow results
  const pollForResults = async (execId: string, maxAttempts = 30): Promise<any> => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        const execution = await workflowService.getExecutionStatus(execId);
        
        if (execution.status === 'completed') {
          const outputs = execution.outputs || execution.attributes?.outputs;
          
          // Extract data from dynamic node structure
          if (outputs && typeof outputs === 'object') {
            const nodeKeys = Object.keys(outputs);
            
            // Handle classify-extract workflows with multiple nodes
            if (nodeKeys.length > 1) {
              // Find classification and extraction nodes
              const classificationNode = nodeKeys.find(key => key.includes('classification'));
              const extractionNode = nodeKeys.find(key => key.includes('extraction'));
              
              const result: any = {};
              
              if (classificationNode && outputs[classificationNode]) {
                const classData = outputs[classificationNode];
                result.classification = classData.extraction_result || classData;
              }
              
              if (extractionNode && outputs[extractionNode]) {
                const extractData = outputs[extractionNode];
                result.extraction = extractData.extraction_result || extractData;
              }
              
              return result;
            }
            
            // Single node workflow
            if (nodeKeys.length === 1) {
              const nodeResult = outputs[nodeKeys[0]];
              
              // Extract the classification/extraction results
              if (nodeResult && nodeResult.extraction_result) {
                // For classification workflows, return the extraction_result directly
                return nodeResult.extraction_result;
              }
              
              // Fallback to returning the full node result
              return nodeResult;
            }
          }
          
          // Fallback to raw outputs if structure is unexpected
          return outputs;
        } else if (execution.status === 'failed') {
          throw new Error(execution.error || execution.attributes?.error || 'Workflow execution failed');
        }
        
        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (err) {
        console.error('Polling error:', err);
        if (i === maxAttempts - 1) {
          throw new Error('Timeout waiting for workflow results');
        }
      }
    }
    
    throw new Error('Workflow execution timeout');
  };

  // Result display components
  const ClassificationResult = ({ data }: { data: any }) => {
    if (!data) return null;
    
    const classification = data.classification || data.document_type || 'Unknown';
    const confidence = data.confidence || data.confidence_score || 0;
    
    return (
      <div className="space-y-4">
        <h4 className="font-semibold flex items-center gap-2">
          <Brain className="w-4 h-4" />
          Classification Result
        </h4>
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-600">Document Type</p>
              <p className="text-xl font-bold capitalize">{classification.replace('_', ' ')}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Confidence</p>
              <p className="text-xl font-bold">{(confidence * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ExtractionResult = ({ data }: { data: any }) => {
    if (!data) return null;
    
    const extractedData = data.extraction_result || data.extracted_data || data;
    
    return (
      <div className="space-y-4">
        <h4 className="font-semibold flex items-center gap-2">
          <Database className="w-4 h-4" />
          Extracted Data
        </h4>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="space-y-2">
            {Object.entries(extractedData).map(([key, value]) => (
              <div key={key} className="flex justify-between py-1 border-b border-green-100 last:border-0">
                <span className="text-sm text-gray-600 capitalize">
                  {key.replace(/_/g, ' ')}
                </span>
                <span className="text-sm font-medium">
                  {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const CombinedResult = ({ data }: { data: any }) => {
    if (!data) return null;
    
    return (
      <div className="space-y-6">
        <ClassificationResult data={data.classification || data} />
        <ExtractionResult data={data.extraction || data} />
      </div>
    );
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* File Upload Section */}
        <div className="space-y-4">
          <Label>1. Upload Document(s)</Label>
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
              ${uploadedFiles.length > 0 ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
            
            {uploadedFiles.length > 0 ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 justify-center mb-4">
                  {uploadedFiles.slice(0, 3).map((fileData) => (
                    <div key={fileData.id} className="relative">
                      {fileData.preview ? (
                        <img 
                          src={fileData.preview} 
                          alt={fileData.file.name} 
                          className="w-16 h-16 object-cover rounded border"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                          <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                  ))}
                  {uploadedFiles.length > 3 && (
                    <div className="w-16 h-16 bg-gray-200 rounded border flex items-center justify-center text-xs text-gray-600">
                      +{uploadedFiles.length - 3}
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-medium">{uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} selected</p>
                  <p className="text-sm text-gray-500">
                    Total: {(uploadedFiles.reduce((total, f) => total + f.file.size, 0) / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <Badge variant="outline" className="mt-2">
                    Click to add more
                  </Badge>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-12 h-12 mx-auto text-gray-400" />
                <p className="text-gray-600">
                  Click to upload or drag and drop multiple documents
                </p>
                <p className="text-sm text-gray-400">
                  Supports JPEG, PNG, PDF (Max 10MB each)
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Uploaded Files List */}
        {uploadedFiles.length > 0 && (
          <div className="space-y-4">
            <Label>Uploaded Files</Label>
            <div className="max-h-32 overflow-y-auto space-y-2">
              {uploadedFiles.map((fileData) => (
                <div key={fileData.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                      <FileText className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">{fileData.file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(fileData.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFile(fileData.id)}
                    className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Workflow Selection */}
        <div className="space-y-4">
          <Label>2. Select Workflow</Label>
          <RadioGroup 
            value={selectedWorkflow} 
            onValueChange={(value) => {
              setSelectedWorkflow(value as WorkflowType);
              // Reset prompt selection when workflow changes
              setSelectedPrompt('');
              setCustomPrompt('');
            }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            {workflowOptions.map((option) => {
              const Icon = option.icon;
              return (
                <div key={option.value} className="relative">
                  <RadioGroupItem
                    value={option.value}
                    id={option.value}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={option.value}
                    className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg cursor-pointer
                      transition-all duration-200 hover:border-blue-300 hover:shadow-md
                      ${selectedWorkflow === option.value 
                        ? 'border-blue-600 bg-blue-100 shadow-lg ring-2 ring-blue-200' 
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                  >
                    <Icon className={`w-8 h-8 ${selectedWorkflow === option.value ? 'text-blue-700' : 'text-gray-600'}`} />
                    <span className={`font-medium ${selectedWorkflow === option.value ? 'text-blue-900' : 'text-gray-900'}`}>
                      {option.label}
                    </span>
                    <span className={`text-xs text-center ${selectedWorkflow === option.value ? 'text-blue-700' : 'text-gray-500'}`}>
                      {option.description}
                    </span>
                    {selectedWorkflow === option.value && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
        </div>

        {/* Prompt Selection (Only for Extract workflow) */}
        {selectedWorkflow === 'extract' && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            <Label>3. Select Extraction Prompt</Label>
            <div className="space-y-4">
              <Select value={selectedPrompt} onValueChange={setSelectedPrompt}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a predefined prompt or use custom..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aadhaar_extraction">Extract Aadhaar Card Information</SelectItem>
                  <SelectItem value="pan_extraction">Extract PAN Card Information</SelectItem>
                  <SelectItem value="passport_extraction">Extract Passport Information</SelectItem>
                  <SelectItem value="general_document">Extract General Document Information</SelectItem>
                  <SelectItem value="custom">Custom Prompt</SelectItem>
                </SelectContent>
              </Select>
              
              {selectedPrompt === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="custom-prompt">Custom Extraction Prompt</Label>
                  <Textarea
                    id="custom-prompt"
                    placeholder="Enter your custom extraction prompt here..."
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    className="min-h-[100px]"
                  />
                </div>
              )}
              
              {selectedPrompt && selectedPrompt !== 'custom' && (
                <Alert>
                  <MessageSquare className="h-4 w-4" />
                  <AlertDescription>
                    Using predefined prompt: <strong>{selectedPrompt.replace('_', ' ').toUpperCase()}</strong>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </motion.div>
        )}

        {/* Execution Summary */}
        {uploadedFiles.length > 0 && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Ready to Execute
            </h3>
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-600">Documents:</span>
                <span className="ml-2 font-medium text-gray-900">{uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''}</span>
              </div>
              <div>
                <span className="text-gray-600">Selected Workflow:</span>
                <span className="ml-2 font-bold text-blue-700 capitalize">
                  {workflowOptions.find(opt => opt.value === selectedWorkflow)?.label}
                </span>
              </div>
              {selectedWorkflow === 'extract' && (selectedPrompt || customPrompt.trim()) && (
                <div>
                  <span className="text-gray-600">Extraction Prompt:</span>
                  <span className="ml-2 font-medium text-gray-900">
                    {selectedPrompt === 'custom' ? 'Custom Prompt' : selectedPrompt?.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
              )}
              <div className="text-xs text-gray-500 mt-2">
                Each document will be processed individually through the selected workflow.
              </div>
            </div>
          </div>
        )}

        {/* Execute Button */}
        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={executeWorkflow}
            disabled={uploadedFiles.length === 0 || processing || (selectedWorkflow === 'extract' && !selectedPrompt && !customPrompt.trim())}
            className="min-w-[200px]"
          >
            {processing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Execute Workflow
              </>
            )}
          </Button>
        </div>

        {/* Progress Bar */}
        {processing && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>{processingStatus || 'Processing documents...'}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Consolidated Results Display */}
        {allResults.length > 0 && !processing && (
          <div className="border rounded-lg p-6 space-y-6">
            <div className="flex items-center gap-2 text-green-600 mb-4">
              <CheckCircle className="w-5 h-5" />
              <span className="font-semibold">
                Processing Complete - {allResults.length} document{allResults.length > 1 ? 's' : ''} processed
              </span>
            </div>
            
            {/* Individual File Results */}
            <div className="space-y-4">
              {allResults.map((fileResult, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {fileResult.file}
                    </h4>
                    {fileResult.error ? (
                      <Badge variant="destructive">Failed</Badge>
                    ) : (
                      <Badge variant="default" className="bg-green-100 text-green-800">Success</Badge>
                    )}
                  </div>
                  
                  {fileResult.error ? (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{fileResult.error}</AlertDescription>
                    </Alert>
                  ) : (
                    <div>
                      {selectedWorkflow === 'classify' && <ClassificationResult data={fileResult.results} />}
                      {selectedWorkflow === 'extract' && <ExtractionResult data={fileResult.results} />}
                      {selectedWorkflow === 'classify-extract' && <CombinedResult data={fileResult.results} />}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Summary */}
            <div className="mt-6 pt-4 border-t bg-gray-50 p-4 rounded">
              <h4 className="font-medium mb-2">Processing Summary</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Total Files:</span>
                  <span className="ml-2 font-medium">{allResults.length}</span>
                </div>
                <div>
                  <span className="text-gray-600">Successful:</span>
                  <span className="ml-2 font-medium text-green-600">
                    {allResults.filter(r => !r.error).length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Failed:</span>
                  <span className="ml-2 font-medium text-red-600">
                    {allResults.filter(r => r.error).length}
                  </span>
                </div>
              </div>
              <div className="mt-2">
                <span className="text-gray-600">Workflow:</span>
                <span className="ml-2 font-medium capitalize">{selectedWorkflow}</span>
              </div>
            </div>
            
            {executionIds.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Execution IDs: {executionIds.join(', ')}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}